import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { UserStatesSelector } from "@/components/ui/state-selector";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { Loader2 } from "lucide-react";
import { User } from "@/types";
import { supabase } from "@/lib/supabaseClient";
import { useTenant } from "@/contexts/TenantContext";
import { SlidePanel } from "@/components/ui/slide-panel";
import { useToast } from "@/components/ui/use-toast";

type UserFormProps = {
  initialData?: Partial<User>;
  roles: Array<{ id: string; name: string }>;
  managers: Array<{ id: string; first_name: string; last_name: string }>;
  allTeams: Array<{ id: string; name: string; type?: string }>;
  onSubmit: (userData: Omit<User, "id">) => Promise<{ success: boolean }>;
  onCancel: () => void;
  isSubmitting: boolean;
  addUserError: string | null;
  onEmailChange?: (email: string) => Promise<{ exists: boolean }>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentUserRole?: string;
  canResetPassword?: boolean;
};

export const UserForm: React.FC<UserFormProps> = ({
  initialData,
  roles,
  managers,
  allTeams,
  onSubmit,
  onCancel,
  isSubmitting,
  addUserError,
  onEmailChange,
  open,
  onOpenChange,
  currentUserRole,
  canResetPassword = false,
}) => {
  const { toast } = useToast();
  const { currentTenant } = useTenant();
  const isEdit = !!initialData?.id;

  // Check if current tenant is Solerro (show Thryve checkbox only for Solerro)
  const isSolerroTenant =
    currentTenant?.company_name?.toLowerCase() === "solerro";
  const [isEmailChecking, setIsEmailChecking] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState<
    Omit<User, "id"> & {
      sales_team_id?: string;
      regular_team_id?: string;
      installer_team_id?: string;
    }
  >({
    first_name: "",
    last_name: "",
    email: "",
    role_id: "",
    phone_number: "",
    thryve_manager_id: "",
    create_in_thryve: true, // Default to true for creating new users
    commercial_or_residential: "residential",
    require_onboarding: false,
    sunstone_access: false,
    team_ids: [],
    sales_team_id: "",
    regular_team_id: "",
    installer_team_id: "",
  });

  // State management for allowed states
  const [residentialStates, setResidentialStates] = useState<string[]>([
    "AR",
    "AZ",
    "CT",
    "CO",
    "IA",
    "KS",
    "KY",
    "LA",
    "MA",
    "ME",
    "MO",
    "NC",
    "NH",
    "NJ",
    "OH",
    "OK",
    "PA",
    "RI",
    "SC",
    "TX",
    "VT",
    "MD",
    "FL",
  ]);
  const [commercialStates, setCommercialStates] = useState<string[]>([
    "CT",
    "IL",
    "MD",
    "MA",
    "NJ",
    "PA",
    "RI",
    "VA",
    "DC",
    "WV",
    "ME",
    "NH",
    "VT",
  ]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [installerPartners, setInstallerPartners] = useState<
    Array<{ id: string; name: string }>
  >([]);

  // Helper functions to filter teams by type - memoized to prevent infinite re-renders
  const salesTeams = useMemo(
    () => allTeams.filter((team) => team.type === "SALES"),
    [allTeams]
  );
  const regularTeams = useMemo(
    () => allTeams.filter((team) => team.type === "INTERNAL_PM" || team.type === "BIZ_DEV"),
    [allTeams]
  );

  // Fetch installer partners
  useEffect(() => {
    const fetchInstallerPartners = async () => {
      try {
        const { data, error } = await supabase
          .from("installer_partners")
          .select("id, company_name")
          .eq("is_active", true)
          .order("company_name");

        if (error) throw error;

        const formattedPartners = data.map((partner) => ({
          id: partner.id,
          name: partner.company_name,
        }));

        setInstallerPartners(formattedPartners);
      } catch (error) {
        console.error("Error fetching installer partners:", error);
      }
    };

    fetchInstallerPartners();
  }, []);

  // Fetch user's allowed states when editing
  useEffect(() => {
    const fetchUserStates = async () => {
      // Default states
      const defaultResidentialStates = [
        "AR",
        "AZ",
        "CT",
        "CO",
        "IA",
        "KS",
        "KY",
        "LA",
        "MA",
        "ME",
        "MO",
        "NC",
        "NH",
        "NJ",
        "OH",
        "OK",
        "PA",
        "RI",
        "SC",
        "TX",
        "VT",
        "MD",
        "FL",
      ];
      const defaultCommercialStates = [
        "CT",
        "IL",
        "MD",
        "MA",
        "NJ",
        "PA",
        "RI",
        "VA",
        "DC",
        "WV",
        "ME",
        "NH",
        "VT",
      ];

      // Reset to defaults first
      setResidentialStates(defaultResidentialStates);
      setCommercialStates(defaultCommercialStates);

      if (!isEdit || !initialData?.id || !currentTenant?.tenant_id || !open)
        return;

      try {
        const { data, error } = await supabase
          .from("allowed_user_states")
          .select("residential_states, commercial_states")
          .eq("user_id", initialData.id)
          .eq("tenant_id", currentTenant.tenant_id)
          .single();

        if (error && error.code !== "PGRST116") {
          // PGRST116 is "not found"
          console.error("Error fetching user states:", error);
          return;
        }

        if (data) {
          // Use user's custom states if they exist and are not empty
          if (
            data.residential_states &&
            Array.isArray(data.residential_states) &&
            data.residential_states.length > 0
          ) {
            setResidentialStates(data.residential_states);
          }
          if (
            data.commercial_states &&
            Array.isArray(data.commercial_states) &&
            data.commercial_states.length > 0
          ) {
            setCommercialStates(data.commercial_states);
          }
        }
      } catch (error) {
        console.error("Error fetching user states:", error);
      }
    };

    fetchUserStates();
  }, [isEdit, initialData?.id, currentTenant?.tenant_id, open]);

  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        first_name: initialData.first_name || "",
        last_name: initialData.last_name || "",
        email: initialData.email || "",
        role_id: initialData.role_id || "",
        phone_number: initialData.phone_number || "",
        thryve_manager_id: initialData.thryve_manager_id || "",
        create_in_thryve: initialData.create_in_thryve ?? false, // Default to false for editing
        commercial_or_residential:
          initialData.commercial_or_residential || "residential",
        require_onboarding: initialData.require_onboarding ?? false,
        sunstone_access: initialData.sunstone_access ?? false,
        team_ids: initialData.team_ids
          ? initialData.team_ids.map((id) => String(id))
          : [], // Ensure team_ids is always an array of strings
        sales_team_id: "", // Will be set when teams are loaded
        regular_team_id: "", // Will be set when teams are loaded
        installer_team_id: "", // Will be set when installer partners are loaded
      }));

      // Reset password reset state when editing a different user
      setShowPasswordReset(false);
      setNewPassword("");
    } else {
      // Reset form for new user creation
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        role_id: "",
        phone_number: "",
        thryve_manager_id: "",
        create_in_thryve: true, // Default to true for new users
        commercial_or_residential: "residential",
        require_onboarding: false,
        sunstone_access: false,
        team_ids: [],
        sales_team_id: "",
        regular_team_id: "",
        installer_team_id: "",
      });
    }
  }, [initialData, open]); // Reset when dialog opens/closes

  // Set team assignments when options are loaded (for edit mode)
  useEffect(() => {
    if (isEdit && initialData && initialData.user_teams) {
      // Find regular team from user_teams that matches available regular teams
      const userRegularTeam = initialData.user_teams?.find((ut) =>
        regularTeams.some((team) => String(team.id) === String(ut.team_id))
      );

      // Find sales team from user_teams that matches available sales teams
      const userSalesTeam = initialData.user_teams?.find((ut) =>
        salesTeams.some((team) => String(team.id) === String(ut.team_id))
      );

      if (userRegularTeam) {
        setFormData((prev) => ({
          ...prev,
          regular_team_id: String(userRegularTeam.team_id),
        }));
      }

      if (userSalesTeam) {
        setFormData((prev) => ({
          ...prev,
          sales_team_id: String(userSalesTeam.team_id),
        }));
      }
    }
  }, [isEdit, initialData, salesTeams, regularTeams]);

  // Set installer partner assignments when options are loaded (for edit mode)
  useEffect(() => {
    if (
      isEdit &&
      initialData &&
      initialData.installer_partners &&
      initialData.installer_partners.length > 0 &&
      installerPartners.length > 0
    ) {
      // Find installer partner that exists in both user's assignments and available options
      const userInstallerPartner = initialData.installer_partners.find((ip) =>
        installerPartners.some((partner) => partner.id === String(ip.id))
      );
      if (userInstallerPartner) {
        setFormData((prev) => ({
          ...prev,
          installer_team_id: String(userInstallerPartner.id),
        }));
      }
    }
  }, [isEdit, initialData, installerPartners]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required";
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = "Last name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.phone_number || !formData.phone_number.trim()) {
      newErrors.phone_number = "Phone number is required";
    } else {
      // Remove all non-digit characters to check length
      const digitsOnly = formData.phone_number.replace(/\D/g, "");
      if (digitsOnly.length < 10) {
        newErrors.phone_number = `Phone number needs ${10 - digitsOnly.length} more digits`;
      } else if (digitsOnly.length > 10) {
        newErrors.phone_number = "Phone number has too many digits";
      }
    }

    // Only require team for new users, not for editing existing users
    if (
      !isEdit &&
      !formData.sales_team_id &&
      !formData.regular_team_id &&
      !formData.installer_team_id
    ) {
      newErrors.teams =
        "At least one team (Internal Team, Sales Team, or Installer Team) is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, "");

    // Format as (###) ###-####
    if (digitsOnly.length <= 3) {
      return digitsOnly;
    } else if (digitsOnly.length <= 6) {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
    } else {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    let processedValue = value;

    // Format phone number as user types
    if (name === "phone_number") {
      processedValue = formatPhoneNumber(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : processedValue,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Handle email validation
    if (name === "email" && onEmailChange) {
      setIsEmailChecking(true);
      onEmailChange(value).finally(() => setIsEmailChecking(false));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Validate required fields when user leaves the field
    if (name === "first_name" && !value.trim()) {
      setErrors((prev) => ({
        ...prev,
        first_name: "First name is required",
      }));
    }

    if (name === "last_name" && !value.trim()) {
      setErrors((prev) => ({
        ...prev,
        last_name: "Last name is required",
      }));
    }

    if (name === "email") {
      if (!value.trim()) {
        setErrors((prev) => ({
          ...prev,
          email: "Email is required",
        }));
      } else if (!/\S+@\S+\.\S+/.test(value)) {
        setErrors((prev) => ({
          ...prev,
          email: "Please enter a valid email",
        }));
      }
    }

    // Validate phone number when user leaves the field
    if (name === "phone_number") {
      const digitsOnly = value.replace(/\D/g, "");
      if (digitsOnly.length === 0) {
        setErrors((prev) => ({
          ...prev,
          phone_number: "Phone number is required",
        }));
      } else if (digitsOnly.length < 10) {
        setErrors((prev) => ({
          ...prev,
          phone_number: `Phone number needs ${10 - digitsOnly.length} more digits`,
        }));
      } else if (digitsOnly.length > 10) {
        setErrors((prev) => ({
          ...prev,
          phone_number: "Phone number has too many digits",
        }));
      }
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user makes a selection
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const saveUserStates = async (userIdOverride?: string) => {
    if (!currentTenant?.tenant_id) return;

    const defaultResidentialStates = [
      "AR",
      "AZ",
      "CT",
      "CO",
      "IA",
      "KS",
      "KY",
      "LA",
      "MA",
      "ME",
      "MO",
      "NC",
      "NH",
      "NJ",
      "OH",
      "OK",
      "PA",
      "RI",
      "SC",
      "TX",
      "VT",
      "MD",
      "FL",
    ];
    const defaultCommercialStates = [
      "CT",
      "IL",
      "MD",
      "MA",
      "NJ",
      "PA",
      "RI",
      "VA",
      "DC",
      "WV",
      "ME",
      "NH",
      "VT",
    ];

    // Check if states differ from defaults
    const residentialStatesDiffer =
      JSON.stringify([...residentialStates].sort()) !==
      JSON.stringify([...defaultResidentialStates].sort());
    const commercialStatesDiffer =
      JSON.stringify([...commercialStates].sort()) !==
      JSON.stringify([...defaultCommercialStates].sort());

    // Get the user ID (either from override, initial data for edit mode, or try to get from email for new users)
    let userId = userIdOverride || (isEdit ? initialData?.id : null);

    // For new users, try to get the user ID by email
    if (!userId && !isEdit && formData.email) {
      try {
        const { data: userData, error: userError } = await supabase
          .from("user_profiles")
          .select("user_id")
          .eq("email", formData.email)
          .eq("tenant_id", currentTenant.tenant_id)
          .single();

        if (!userError && userData) {
          userId = userData.user_id;
        }
      } catch (error) {
        console.error("Error finding user by email:", error);
      }
    }

    if (!userId) {
      return;
    }

    try {
      if (residentialStatesDiffer || commercialStatesDiffer) {
        // Save custom states
        const { error } = await supabase.from("allowed_user_states").upsert(
          {
            user_id: userId,
            tenant_id: currentTenant.tenant_id,
            residential_states: residentialStates,
            commercial_states: commercialStates,
            updated_at: new Date().toISOString(),
          },
          {
            onConflict: "user_id,tenant_id",
          }
        );

        if (error) {
          console.error("Error saving user states:", error);
          toast({
            title: "Warning",
            description:
              "User created successfully but state preferences could not be saved.",
            variant: "destructive",
          });
        }
      } else {
        // States match defaults, remove any existing custom record
        const { error } = await supabase
          .from("allowed_user_states")
          .delete()
          .eq("user_id", userId)
          .eq("tenant_id", currentTenant.tenant_id);

        if (error && error.code !== "PGRST116") {
          // Ignore "not found" errors
          console.error("Error removing user states:", error);
        }
      }
    } catch (error) {
      console.error("Error managing user states:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      // Show toast with validation errors
      const errorMessages = Object.values(errors).filter(Boolean);
      if (errorMessages.length > 0) {
        toast({
          title: "Please complete required fields",
          description: errorMessages.join(" • "),
          variant: "destructive",
        });
      }
      return;
    }

    // Validate password reset if enabled
    if (showPasswordReset && newPassword.length < 8) {
      toast({
        title: "Error",
        description: "Password must be at least 8 characters long",
        variant: "destructive",
      });
      return;
    }

    // Combine sales, regular, and installer team selections into team_ids
    const selectedTeamIds = [
      formData.sales_team_id,
      formData.regular_team_id,
      formData.installer_team_id,
    ].filter(
      (id): id is string =>
        Boolean(id) && typeof id === "string" && id.trim() !== ""
    ); // Remove empty strings and undefined

    // Include password in submission data if password reset is enabled
    // Also include states for new users
    const submissionData = {
      ...formData,
      team_ids: selectedTeamIds,
      ...(showPasswordReset && newPassword && { password: newPassword }),
      ...(!isEdit && {
        residentialStates,
        commercialStates,
      }),
    };

    // Remove the separate team fields from submission
    delete submissionData.sales_team_id;
    delete submissionData.regular_team_id;
    delete submissionData.installer_team_id;

    const result = await onSubmit(submissionData);

    if (result.success) {
      // For edit mode, save allowed states separately
      if (isEdit) {
        await saveUserStates();
      }
      // For new users, states are saved in the createUser function in useUsers hook

      onOpenChange(false);
      // Reset form on successful submission for create mode
      if (!isEdit) {
        setFormData({
          first_name: "",
          last_name: "",
          email: "",
          role_id: "",
          phone_number: "",
          team_ids: [],
          sales_team_id: "",
          regular_team_id: "",
          installer_team_id: "",
          thryve_manager_id: "",
          create_in_thryve: true, // Default to true for new users
          commercial_or_residential: "residential",
          require_onboarding: false,
          sunstone_access: false,
        });
        // Reset states to defaults for new users
        setResidentialStates([
          "AR",
          "AZ",
          "CT",
          "CO",
          "IA",
          "KS",
          "KY",
          "LA",
          "MA",
          "ME",
          "MO",
          "NC",
          "NH",
          "NJ",
          "OH",
          "OK",
          "PA",
          "RI",
          "SC",
          "TX",
          "VT",
          "MD",
          "FL",
        ]);
        setCommercialStates([
          "CT",
          "IL",
          "MD",
          "MA",
          "NJ",
          "PA",
          "RI",
          "VA",
          "DC",
          "WV",
          "ME",
          "NH",
          "VT",
        ]);
      }

      // Reset password reset state on successful submission
      if (showPasswordReset) {
        setShowPasswordReset(false);
        setNewPassword("");
        toast({
          title: "Success",
          description:
            "Password has been reset. User will be prompted to change it on next login.",
        });
      }
    }
  };

  return (
    <SlidePanel
      isOpen={open}
      onClose={() => onOpenChange(false)}
      title={isEdit ? "Edit User" : "Add User"}
      width="xl"
      topOffset="4.5rem"
      showEdgeCloseButton={true}
      footer={
        <div className="flex justify-end space-x-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Button
                    type="submit"
                    disabled={
                      isSubmitting ||
                      !formData.first_name ||
                      !formData.last_name ||
                      !formData.email ||
                      !formData.phone_number ||
                      !/^\d{10}$/.test(
                        formData.phone_number.replace(/\D/g, "")
                      ) ||
                      (!isEdit &&
                        !formData.sales_team_id &&
                        !formData.regular_team_id &&
                        !formData.installer_team_id) ||
                      (showPasswordReset && newPassword.length < 8)
                    }
                    onClick={handleSubmit}
                    className="w-full sm:w-auto"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {isEdit ? "Updating..." : "Creating..."}
                      </>
                    ) : (
                      <>{isEdit ? "Update User" : "Add User"}</>
                    )}
                  </Button>
                </div>
              </TooltipTrigger>
              {(isSubmitting ||
                !formData.first_name ||
                !formData.last_name ||
                !formData.email ||
                !formData.phone_number ||
                !/^\d{10}$/.test(formData.phone_number.replace(/\D/g, "")) ||
                (!formData.sales_team_id &&
                  !formData.regular_team_id &&
                  !formData.installer_team_id) ||
                (showPasswordReset && newPassword.length < 8)) && (
                <TooltipContent side="top" className="max-w-xs">
                  <div className="space-y-1">
                    <p className="font-semibold">
                      Please complete the following:
                    </p>
                    <ul className="text-xs space-y-0.5">
                      {!formData.first_name && (
                        <li>• First name is required</li>
                      )}
                      {!formData.last_name && <li>• Last name is required</li>}
                      {!formData.email && <li>• Email address is required</li>}
                      {!formData.phone_number && (
                        <li>• Phone number is required</li>
                      )}
                      {formData.phone_number &&
                        !/^\d{10}$/.test(
                          formData.phone_number.replace(/\D/g, "")
                        ) && <li>• Phone number must be 10 digits</li>}
                      {!isEdit &&
                        !formData.sales_team_id &&
                        !formData.regular_team_id &&
                        !formData.installer_team_id && (
                          <li>• At least one team assignment is required</li>
                        )}
                      {showPasswordReset && newPassword.length < 8 && (
                        <li>• Password must be at least 8 characters</li>
                      )}
                    </ul>
                  </div>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid gap-3 py-4">
          {/* Contact Information */}
          <div className="border border-border rounded-lg p-4">
            <Label className="text-sm font-medium text-foreground mb-3 block">
              User Information
            </Label>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting}
                    placeholder="Enter first name"
                    className="w-full"
                  />
                  {errors.first_name && (
                    <p className="text-sm text-destructive">
                      {errors.first_name}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting}
                    placeholder="Enter last name"
                    className="w-full"
                  />
                  {errors.last_name && (
                    <p className="text-sm text-destructive">
                      {errors.last_name}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <div className="relative">
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting || isEdit}
                    placeholder="Enter email address"
                    className={isEdit ? "bg-muted w-full" : "w-full"}
                    autoComplete="off"
                  />
                  {isEmailChecking && (
                    <div className="absolute right-2 top-2.5">
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                  )}
                </div>
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email}</p>
                )}
                {addUserError && (
                  <p className="text-sm text-destructive">{addUserError}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="phone_number">Phone *</Label>
                  <Input
                    id="phone_number"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting}
                    placeholder="(123) 456-7890"
                    className="w-full"
                  />
                  {errors.phone_number && (
                    <p className="text-sm text-destructive">
                      {errors.phone_number}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role_id">Role *</Label>
                  <SearchableSelect
                    id="role_id"
                    value={formData.role_id}
                    onValueChange={(value) =>
                      handleSelectChange("role_id", value)
                    }
                    options={roles.map((role) => ({
                      label: role.name,
                      value: role.id,
                    }))}
                    placeholder="Select role"
                    disabled={isSubmitting}
                    className="w-full"
                    inDialog={false}
                  />
                  {errors.role_id && (
                    <p className="text-sm text-destructive">{errors.role_id}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Team Assignments */}
          <div className="border border-border rounded-lg p-4">
            <Label className="text-sm font-medium text-foreground mb-3 block">
              Team Assignments *
              <span className="text-xs font-normal text-muted-foreground ml-2">
                (at least one required)
              </span>
            </Label>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="regular_team_id">Internal Team</Label>
                  <SearchableSelect
                    id="regular_team_id"
                    value={formData.regular_team_id || ""}
                    onValueChange={(value) => {
                      setFormData((prev) => ({
                        ...prev,
                        regular_team_id: value,
                      }));

                      // Clear teams error when user makes a selection
                      if (errors.teams) {
                        setErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.teams;
                          return newErrors;
                        });
                      }
                    }}
                    options={regularTeams.map((team) => ({
                      label: team.name,
                      value: String(team.id),
                    }))}
                    placeholder="Select internal team"
                    disabled={isSubmitting}
                    className="w-full"
                    inDialog={false}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sales_team_id">Sales Team</Label>
                  <SearchableSelect
                    id="sales_team_id"
                    value={formData.sales_team_id || ""}
                    onValueChange={(value) => {
                      setFormData((prev) => ({
                        ...prev,
                        sales_team_id: value,
                      }));

                      // Clear teams error when user makes a selection
                      if (errors.teams) {
                        setErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.teams;
                          return newErrors;
                        });
                      }
                    }}
                    options={salesTeams.map((team) => ({
                      label: team.name,
                      value: String(team.id),
                    }))}
                    placeholder="Select sales team"
                    disabled={isSubmitting}
                    className="w-full"
                    inDialog={false}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="installer_team_id">Installer Partner</Label>
                  <SearchableSelect
                    id="installer_team_id"
                    value={formData.installer_team_id || ""}
                    onValueChange={(value) => {
                      setFormData((prev) => ({
                        ...prev,
                        installer_team_id: value,
                      }));

                      // Clear teams error when user makes a selection
                      if (errors.teams) {
                        setErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.teams;
                          return newErrors;
                        });
                      }
                    }}
                    options={installerPartners.map((partner) => ({
                      label: partner.name,
                      value: String(partner.id),
                    }))}
                    placeholder="Select installer partner"
                    disabled={isSubmitting}
                    className="w-full"
                    inDialog={false}
                  />
                  {errors.teams && (
                    <p className="text-sm text-destructive">{errors.teams}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="thryve_manager_id">Manager</Label>
                  <SearchableSelect
                    id="thryve_manager_id"
                    value={formData.thryve_manager_id || ""}
                    onValueChange={(value) => {
                      handleSelectChange("thryve_manager_id", value);
                    }}
                    options={managers.map((manager) => ({
                      label: `${manager.first_name} ${manager.last_name}`,
                      value: manager.id,
                    }))}
                    placeholder="Select manager"
                    disabled={isSubmitting}
                    className="w-full"
                    inDialog={false}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Password Reset Section for Admin/Superadmin */}
          {isEdit && canResetPassword && (
            <div className="space-y-2">
              <Label>Password Reset</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowPasswordReset(!showPasswordReset)}
                disabled={isSubmitting}
                className="w-full"
              >
                {showPasswordReset ? "Cancel Reset" : "Reset Password"}
              </Button>

              {showPasswordReset && (
                <div className="space-y-2">
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password (min 8 characters)"
                      className="pr-20"
                    />
                    <div className="absolute inset-y-0 right-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="text-sm text-muted-foreground hover:underline"
                      >
                        {showPassword ? "Hide" : "Show"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(newPassword);
                          toast({
                            title: "Success",
                            description: "Password copied!",
                            duration: 1000,
                          });
                        }}
                        className="text-sm text-muted-foreground hover:underline"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    User will be prompted to change password on next login
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Primary Goal Section */}
          <div className="border border-border rounded-lg p-4">
            <Label className="text-sm font-medium text-foreground mb-3 block">
              Primary Goal
            </Label>
            <div className="flex items-center gap-6">
              <div className="flex items-center space-x-2">
                <input
                  id="residential"
                  name="commercial_or_residential"
                  type="radio"
                  value="residential"
                  checked={formData.commercial_or_residential === "residential"}
                  onChange={() =>
                    setFormData((prev) => ({
                      ...prev,
                      commercial_or_residential: "residential",
                    }))
                  }
                  className="h-4 w-4 text-primary focus:ring-primary border-input"
                  disabled={isSubmitting}
                />
                <Label htmlFor="residential" className="text-sm cursor-pointer">
                  Residential
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  id="commercial"
                  name="commercial_or_residential"
                  type="radio"
                  value="commercial"
                  checked={formData.commercial_or_residential === "commercial"}
                  onChange={() =>
                    setFormData((prev) => ({
                      ...prev,
                      commercial_or_residential: "commercial",
                    }))
                  }
                  className="h-4 w-4 text-primary focus:ring-primary border-input"
                  disabled={isSubmitting}
                />
                <Label htmlFor="commercial" className="text-sm cursor-pointer">
                  Commercial
                </Label>
              </div>
            </div>
          </div>

          {/* Permissions & Access Section */}
          <div className="border border-border rounded-lg p-4">
            <Label className="text-sm font-medium text-foreground mb-3 block">
              Permissions & Access
            </Label>
            <div className="space-y-3">
              {isSolerroTenant && (
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="create_in_thryve"
                    checked={formData.create_in_thryve}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({
                        ...prev,
                        create_in_thryve: checked as boolean,
                      }))
                    }
                    disabled={isSubmitting}
                    className="h-4 w-4"
                  />
                  <Label
                    htmlFor="create_in_thryve"
                    className="text-sm cursor-pointer"
                  >
                    Create in Thryve and Everee
                  </Label>
                </div>
              )}

              <div className="flex items-center space-x-3">
                <Checkbox
                  id="require_onboarding"
                  checked={formData.require_onboarding}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({
                      ...prev,
                      require_onboarding: checked as boolean,
                    }))
                  }
                  disabled={isSubmitting}
                  className="h-4 w-4"
                />
                <Label
                  htmlFor="require_onboarding"
                  className="text-sm cursor-pointer"
                >
                  Require Onboarding
                </Label>
              </div>

              <div className="flex items-center space-x-3">
                <Checkbox
                  id="sunstone_access"
                  checked={formData.sunstone_access}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({
                      ...prev,
                      sunstone_access: checked as boolean,
                    }))
                  }
                  disabled={isSubmitting}
                  className="h-4 w-4"
                />
                <Label
                  htmlFor="sunstone_access"
                  className="text-sm cursor-pointer"
                >
                  Sunstone Access
                </Label>
              </div>
            </div>
          </div>

          {/* Allowed States Section */}
          <div className="border border-border rounded-lg p-4">
            <h3 className="text-sm font-medium text-foreground mb-4">
              Allowed States
            </h3>
            <UserStatesSelector
              residentialStates={residentialStates}
              commercialStates={commercialStates}
              onResidentialStatesChange={setResidentialStates}
              onCommercialStatesChange={setCommercialStates}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </form>
    </SlidePanel>
  );
};
